import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api-client.js';

/**
 * OAuth Callback Page — runs inside the popup window.
 *
 * Flow:
 * 1. FB redirects here with ?code=XXX&state=YYY
 * 2. We POST code to our API → get list of user's FB pages
 * 3. If one page → auto-select. If multiple → show picker.
 * 4. POST selected page to /facebook/select-page
 * 5. Signal parent window and close popup.
 */

interface FBPage {
  id: string;
  name: string;
  access_token?: string;
}

type Stage = 'exchanging' | 'select-page' | 'saving' | 'done' | 'error';

export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('exchanging');
  const [pages, setPages] = useState<FBPage[]>([]);
  const [pageTokens, setPageTokens] = useState<FBPage[]>([]);
  const [error, setError] = useState('');

  // Detect if we're in a popup or a redirect flow
  const isPopup = !!window.opener;
  const returnPath = sessionStorage.getItem('acp-oauth-return') || '/connect';

  const signalParentAndClose = useCallback(() => {
    if (isPopup) {
      try {
        window.opener?.postMessage({ type: 'oauth-success' }, '*');
      } catch { /* cross-origin safety */ }
      setTimeout(() => window.close(), 500);
    } else {
      // Redirect flow (mobile) — navigate back to connect page
      sessionStorage.removeItem('acp-oauth-return');
      navigate(returnPath, { replace: true });
    }
  }, [isPopup, navigate, returnPath]);

  // Step 1: Exchange code for token + get pages
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (!code) {
      setError('No authorization code received from Facebook.');
      setStage('error');
      return;
    }

    const redirectUri = window.location.origin + window.location.pathname;

    api.post<{ pages: FBPage[]; user_token: string; _page_tokens: FBPage[] }>(
      '/marketplace/facebook/callback',
      { code, redirect_uri: redirectUri }
    ).then((res) => {
      if (!res.success || !res.data) {
        setError(res.error ?? 'Failed to connect to Facebook.');
        setStage('error');
        return;
      }

      const pageList = res.data.pages ?? [];
      const tokenList = res.data._page_tokens ?? [];
      setPages(pageList);
      setPageTokens(tokenList);

      if (pageList.length === 0) {
        setError('No Facebook Pages found. You need a Facebook Page to post listings.');
        setStage('error');
      } else if (pageList.length === 1) {
        // Auto-select the only page
        selectPage(pageList[0], tokenList);
      } else {
        setStage('select-page');
      }
    }).catch(() => {
      setError('Network error connecting to Facebook.');
      setStage('error');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 2: Select a page and finalize connection
  const selectPage = useCallback(async (page: FBPage, tokens?: FBPage[]) => {
    setStage('saving');
    const tokenSource = tokens ?? pageTokens;
    const pageWithToken = tokenSource.find(p => p.id === page.id);
    const pageToken = pageWithToken?.access_token ?? '';

    const res = await api.post('/marketplace/facebook/select-page', {
      page_id: page.id,
      page_name: page.name,
      page_access_token: pageToken,
      connect_marketplace: true,
    });

    if (res.success) {
      setStage('done');
      signalParentAndClose();
    } else {
      setError(res.error ?? 'Failed to save page connection.');
      setStage('error');
    }
  }, [pageTokens, signalParentAndClose]);

  return (
    <div className="min-h-screen bg-surface-900 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">

        {/* Exchanging code */}
        {stage === 'exchanging' && (
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-[#1877F2] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white font-semibold">Connecting to Facebook...</p>
            <p className="text-surface-500 text-sm mt-1">Exchanging authorization</p>
          </div>
        )}

        {/* Page picker */}
        {stage === 'select-page' && (
          <div>
            <h2 className="text-xl font-bold text-white text-center mb-2">Select a Page</h2>
            <p className="text-surface-400 text-sm text-center mb-4">
              Which Facebook Page should we post your listings to?
            </p>
            <div className="space-y-2">
              {pages.map((page) => (
                <button
                  key={page.id}
                  onClick={() => selectPage(page)}
                  className="w-full p-4 rounded-xl bg-surface-800 hover:bg-surface-700 transition-colors text-left"
                >
                  <p className="text-white font-medium">{page.name}</p>
                  <p className="text-surface-500 text-xs mt-0.5">Page ID: {page.id}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Saving */}
        {stage === 'saving' && (
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-[#1877F2] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white font-semibold">Saving connection...</p>
          </div>
        )}

        {/* Done */}
        {stage === 'done' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-white font-semibold">Facebook Connected!</p>
            <p className="text-surface-400 text-sm mt-1">
              {isPopup ? 'This window will close automatically.' : 'Redirecting...'}
            </p>
            <button
              onClick={signalParentAndClose}
              className="mt-4 text-sm text-[#1877F2] hover:underline"
            >
              {isPopup ? 'Close now' : 'Back to Connect'}
            </button>
          </div>
        )}

        {/* Error */}
        {stage === 'error' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-white font-semibold mb-2">Connection Failed</p>
            <p className="text-surface-400 text-sm">{error}</p>
            <button
              onClick={() => isPopup ? window.close() : navigate(returnPath, { replace: true })}
              className="mt-4 px-6 py-2 rounded-xl bg-surface-700 text-white text-sm hover:bg-surface-600 transition-colors"
            >
              {isPopup ? 'Close' : 'Back to Connect'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
